import React, { useEffect, useState, useRef } from 'react';
import { Plus, Download, Search, Upload, FileSpreadsheet, LogIn, LogOut, ScrollText, Check, AlertCircle, X } from 'lucide-react';
import { Contributor, Solution, AvailabilityMap, AvailabilityStatus, Region, REGIONS, ExperienceLevel, ExperienceLevelMap, EXPERIENCE_LABELS } from '../types';
import { AvailabilityGrid } from './AvailabilityGrid';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Modal } from './ui/Modal';
import { useAdobeImsAuth } from '../auth/useAdobeImsAuth';
import { logAvailabilityChange, logExperienceLevelChange, upsertContributorRecord, deleteContributorRecord, reorderContributorRecords, upsertSolutionRecord, deleteSolutionRecord, reorderSolutionRecords, fetchInitialStateFromDatabase, fetchCurrentWeekSchedule, fetchNextWeekSchedule, updateDailyAvailability, fetchAuditLog } from '../utils/database';
import { ContributorWeekView } from './ContributorWeekView';
import { normalizeHydratedSolutions, reindexSolutions } from '../utils/solutionOrder';
import { normalizeHydratedContributors, reindexContributors } from '../utils/contributorOrder';
import { isLongLeaveRange, calendarDaysInclusive } from '../utils/longLeave';
function stableSlugId(input: string) {
  const normalized = input.trim().toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const slug = normalized.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  if (slug) {
    return slug;
  }
  let hash = 0;
  for (const char of normalized || input.trim().toLowerCase()) {
    hash = hash * 31 + char.codePointAt(0)! >>> 0;
  }
  return `id-${hash.toString(16)}`;
}
const STATUS_LABELS: Record<AvailabilityStatus, string> = {
  available: 'Qualified and Available',
  limited: 'Qualified not Available',
  unavailable: 'Not Qualified'
};
const STATUS_COLORS: Record<AvailabilityStatus, {
  bg: string;
  text: string;
  border: string;
}> = {
  available: {
    bg: 'bg-green-100',
    text: 'text-green-700',
    border: 'border-green-300'
  },
  limited: {
    bg: 'bg-yellow-100',
    text: 'text-yellow-700',
    border: 'border-yellow-300'
  },
  unavailable: {
    bg: 'bg-red-100',
    text: 'text-red-700',
    border: 'border-red-300'
  }
};
export function Dashboard() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    accessLevel,
    canManageData,
    error: authError,
    isLoading: isAuthLoading,
    isReady: isAuthReady,
    isSignedIn,
    signIn,
    signOut,
    user
  } = useAdobeImsAuth();
  const isAdmin = accessLevel === 'superadmin' || accessLevel === 'admin';
  const hasDashboardDataAccess = isSignedIn && accessLevel !== 'unregistered' && accessLevel !== 'anonymous';
  // Filter state
  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [selectedSolutionId, setSelectedSolutionId] = useState<string | null>(null);
  const [selectedStatusFilter, setSelectedStatusFilter] = useState<AvailabilityStatus | null>(null);
  const [selectedExperienceFilter, setSelectedExperienceFilter] = useState<ExperienceLevel | null>(null);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState<Region | null>(null);
  const [contributors, setContributors] = useState<Contributor[]>([]);
  const [solutions, setSolutions] = useState<Solution[]>([]);
  const [availability, setAvailability] = useState<AvailabilityMap>({});
  const [experienceLevels, setExperienceLevels] = useState<ExperienceLevelMap>({});
  const [weekViewContributorId, setWeekViewContributorId] = useState<string | null>(null); // full-grid weekly view (name click)
  const [weekPanelContributorId, setWeekPanelContributorId] = useState<string | null>(null); // slide-over panel (icon click)
  const [isHydrating, setIsHydrating] = useState(true);
  const [dbFeedback, setDbFeedback] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);
  const dbFeedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notifyDbResult = (result: {
    success: boolean;
    error?: string;
  }) => {
    if (dbFeedbackTimer.current) clearTimeout(dbFeedbackTimer.current);
    if (result.success) {
      setDbFeedback({
        type: 'success',
        message: 'Saved'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 2000);
    } else {
      setDbFeedback({
        type: 'error',
        message: 'Save failed — please try again'
      });
      dbFeedbackTimer.current = setTimeout(() => setDbFeedback(null), 5000);
    }
  };
  useEffect(() => {
    let isCancelled = false;
    if (!isAuthReady) {
      setIsHydrating(true);
      return () => {
        isCancelled = true;
      };
    }
    if (!isSignedIn) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    if (isSignedIn && !hasDashboardDataAccess) {
      if (!isCancelled) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
      }
      return () => {
        isCancelled = true;
      };
    }
    const loadInitialState = async () => {
      setIsHydrating(true);
      const databaseState = await fetchInitialStateFromDatabase();
      if (isCancelled) {
        return;
      }

      // Security: never fall back to sessionStorage or buildInitialState() when db-get-state
      // fails or returns 403. Client-spoofed accessLevel could otherwise load demo/cached data
      // without a successful server-authorized read.
      if (!databaseState) {
        setContributors([]);
        setSolutions([]);
        setAvailability({});
        setExperienceLevels({});
        setIsHydrating(false);
        return;
      }

      // Normalize OOO flags against today's date so stale isOutOfOffice values are corrected.
      const todayStr = new Date().toISOString().split('T')[0];
      const rawContributors = normalizeHydratedContributors(databaseState.contributors);
      const normalizedContributors = rawContributors.map(c => {
        if (c.oooStartDate && c.oooEndDate) {
          const shouldBeOOO = c.oooStartDate <= todayStr && c.oooEndDate >= todayStr;
          if (c.isOutOfOffice !== shouldBeOOO) return {
            ...c,
            isOutOfOffice: shouldBeOOO
          };
        }
        return c;
      });
      setContributors(normalizedContributors);
      setSolutions(normalizeHydratedSolutions(databaseState.solutions));
      setAvailability(databaseState.availability);
      setExperienceLevels(databaseState.experienceLevels);
      setIsHydrating(false);
    };
    loadInitialState();
    return () => {
      isCancelled = true;
    };
  }, [isAuthReady, isSignedIn, accessLevel]);

  // Modal States
  const [isAddICOpen, setIsAddICOpen] = useState(false);
  const [isAddSolutionOpen, setIsAddSolutionOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isEditContributorOpen, setIsEditContributorOpen] = useState(false);
  const [isEditSolutionOpen, setIsEditSolutionOpen] = useState(false);
  const [newICName, setNewICName] = useState('');
  const [newICEmail, setNewICEmail] = useState('');
  const [newICRegion, setNewICRegion] = useState<Region>('USA');
  const [newICNameError, setNewICNameError] = useState('');
  const [newICEmailError, setNewICEmailError] = useState('');
  const [newSolutionName, setNewSolutionName] = useState('');
  const [newSolutionNameError, setNewSolutionNameError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [importError, setImportError] = useState<string>('');
  const [editingContributor, setEditingContributor] = useState<Contributor | null>(null);
  const [editingSolution, setEditingSolution] = useState<Solution | null>(null);
  const [editContributorName, setEditContributorName] = useState('');
  const [editContributorRegion, setEditContributorRegion] = useState<Region>('USA');
  const [editContributorDisplay, setEditContributorDisplay] = useState(true);
  const [editSolutionName, setEditSolutionName] = useState('');
  // OOO modal state
  const [isOOOModalOpen, setIsOOOModalOpen] = useState(false);
  const [oooTargetContributorId, setOOOTargetContributorId] = useState<string | null>(null);
  const [oooStartDate, setOOOStartDate] = useState('');
  const [oooEndDate, setOOOEndDate] = useState('');
  const [oooError, setOOOError] = useState('');
  const requestManagementAccess = () => {
    if (!canManageData && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return canManageData;
  };
  const requestAdminAccess = () => {
    if (!isAdmin && !isSignedIn && !isAuthLoading) {
      void signIn();
    }
    return isAdmin;
  };
  const handleAuthButtonClick = () => {
    if (isSignedIn) {
      void signOut();
      return;
    }
    void signIn();
  };
  useEffect(() => {
    if (canManageData) {
      if (isAdmin) {
        return;
      }
      setIsAddICOpen(false);
      setIsAddSolutionOpen(false);
      setIsImportOpen(false);
      return;
    }
    setIsAddICOpen(false);
    setIsAddSolutionOpen(false);
    setIsImportOpen(false);
    setIsEditContributorOpen(false);
    setIsEditSolutionOpen(false);
    setIsOOOModalOpen(false);
  }, [canManageData, isAdmin]);
  // Download current data as CSV (for editing and re-importing)
  const handleDownloadCurrentData = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const rows = contributors.map(contributor => {
      const cells = solutions.map(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const level = experienceLevels[contributor.id]?.[solution.id];
        return level ? `${status}:${level}` : status;
      });
      return [contributor.name, contributor.region, ...cells].join(',');
    });
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const date = new Date().toISOString().split('T')[0];
    link.download = `availability-data-${date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };
  const [isExportingLog, setIsExportingLog] = useState(false);
  const handleDownloadAuditLog = async () => {
    if (!requestAdminAccess()) return;
    setIsExportingLog(true);
    try {
      const entries = await fetchAuditLog();
      if (!entries || entries.length === 0) {
        alert('No audit log entries found.');
        return;
      }

      // Collect all unique keys across all entries for CSV columns
      const keySet = new Set<string>();
      for (const entry of entries) {
        for (const key of Object.keys(entry)) keySet.add(key);
      }
      // Preferred column order; any remaining keys are appended
      const orderedKeys = ['_id', 'timestamp', 'actionType', 'actorId', 'contributorId', 'contributorName', 'region', 'solutionId', 'solutionName', 'status', 'startDate', 'endDate', 'notes'];
      const extraKeys = [...keySet].filter(k => !orderedKeys.includes(k)).sort();
      const columns = [...orderedKeys.filter(k => keySet.has(k)), ...extraKeys];
      const escape = (v: any) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const csvRows = [columns.join(','), ...entries.map(e => columns.map(k => escape(e[k])).join(','))];
      const blob = new Blob([csvRows.join('\n')], {
        type: 'text/csv'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-log-${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setIsExportingLog(false);
    }
  };

  // Download template CSV
  const handleDownloadTemplate = () => {
    if (!requestAdminAccess()) return;
    const headers = ['Contributor', 'Region', ...solutions.map(s => s.name)];
    const exampleRow = ['John Doe', 'USA', ...solutions.map((_, i) => {
      // Show varied examples
      if (i === 0) return 'available:L3';
      if (i === 1) return 'limited:L2';
      if (i === 2) return 'unavailable';
      return 'available:L1';
    })];
    const csvContent = [headers.join(','), exampleRow.join(','), ['Jane Smith', 'EMEA', ...solutions.map(() => '')].join(',')].join('\n');
    const blob = new Blob([csvContent], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'availability-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };
  // Parse and import CSV
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!requestAdminAccess()) return;
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const text = e.target?.result as string;
        const lines = text.split('\n').filter(line => line.trim());
        if (lines.length < 2) {
          setImportError('File must contain at least a header row and one data row');
          return;
        }
        // Parse header
        const headers = lines[0].split(',').map(h => h.trim());
        const contributorIndex = headers.findIndex(h => h.toLowerCase() === 'contributor');
        const regionIndex = headers.findIndex(h => h.toLowerCase() === 'region');
        if (contributorIndex === -1 || regionIndex === -1) {
          setImportError('CSV must have "Contributor" and "Region" columns');
          return;
        }
        const solutionHeaders = headers.slice(2); // Everything after Contributor and Region
        // Create solutions from headers
        const newSolutions: Solution[] = reindexSolutions(solutionHeaders.map(name => ({
          // id: crypto.randomUUID(),
          id: stableSlugId(name.trim()),
          name: name.trim()
        })));
        // Parse data rows
        const newContributors: Contributor[] = [];
        const newAvailability: AvailabilityMap = {};
        const newExperienceLevels: ExperienceLevelMap = {};
        const validLevels = ['L1', 'L2', 'L3', 'L4'];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim());
          const name = values[contributorIndex];
          const region = values[regionIndex] as Region;
          if (!name || !REGIONS.includes(region)) continue;
          // const contributorId = crypto.randomUUID();
          const contributorId = stableSlugId(name);
          newContributors.push({
            id: contributorId,
            name,
            email: '',
            region
          });
          // Parse availability and experience level for each solution
          // Format: "status" or "status:level" (e.g., "available:L2")
          newAvailability[contributorId] = {};
          newExperienceLevels[contributorId] = {};
          solutionHeaders.forEach((_, idx) => {
            const cellValue = values[idx + 2]?.trim() || '';
            const solutionId = newSolutions[idx].id;
            const parts = cellValue.split(':').map(p => p.trim());
            const statusPart = parts[0]?.toLowerCase();
            const levelPart = parts[1]?.toUpperCase();
            // Parse status
            if (statusPart === 'available' || statusPart === 'limited' || statusPart === 'unavailable') {
              newAvailability[contributorId][solutionId] = statusPart as AvailabilityStatus;
            } else {
              newAvailability[contributorId][solutionId] = 'unavailable';
            }
            // Parse experience level
            if (levelPart && validLevels.includes(levelPart)) {
              newExperienceLevels[contributorId][solutionId] = levelPart as ExperienceLevel;
            }
          });
        }
        // Update state
        setContributors(newContributors);
        setSolutions(newSolutions);
        setAvailability(newAvailability);
        setExperienceLevels(newExperienceLevels);
        setImportError('');
        setIsImportOpen(false);
        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        setImportError('Failed to parse CSV file. Please check the format.');
        console.error('CSV parse error:', error);
      }
    };
    reader.readAsText(file);
  };
  // Helper to update lastUpdated timestamp for a contributor
  const touchContributor = (contributorId: string) => {
    setContributors(prev => prev.map(c => c.id === contributorId ? {
      ...c,
      lastUpdated: new Date().toISOString()
    } : c));
  };
  const saveContributorRecord = (contributor: Contributor) => {
    upsertContributorRecord(contributor).then(notifyDbResult);
  };
  const saveSolutionOrder = (orderedSolutions: Solution[]) => {
    if (orderedSolutions.length === 0) return;
    reorderSolutionRecords(orderedSolutions.map(solution => ({
      id: solution.id,
      displayOrder: solution.displayOrder
    }))).then(notifyDbResult);
  };
  const saveContributorOrder = (orderedContributors: Contributor[]) => {
    if (orderedContributors.length === 0) return;
    reorderContributorRecords(orderedContributors.map(contributor => ({
      id: contributor.id,
      displayOrder: contributor.displayOrder
    }))).then(notifyDbResult);
  };
  // Handlers
  const handleToggleAvailability = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);

    // The grid displays and edits base availability only (weekly overrides are
    // managed in the week view and never surface here). Cycle from base.
    const currentStatus = availability[contributorId]?.[solutionId] ?? 'unavailable';
    const statusCycle: Record<AvailabilityStatus, AvailabilityStatus> = {
      unavailable: 'limited',
      limited: 'available',
      available: 'unavailable'
    };
    const newStatus = statusCycle[currentStatus];
    setAvailability(prev => ({
      ...prev,
      [contributorId]: {
        ...(prev[contributorId] ?? {}),
        [solutionId]: newStatus
      }
    }));
    if (contributor && solution) {
      logAvailabilityChange(contributor, solution, newStatus, experienceLevels[contributorId]?.[solutionId]).then(notifyDbResult);
    }
    touchContributor(contributorId);
  };
  const handleCycleExperienceLevel = async (contributorId: string, solutionId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const contributorLevels = experienceLevels[contributorId] || {};
    const currentLevel = contributorLevels[solutionId];
    const cycle: Record<string, ExperienceLevel | undefined> = {
      undefined: 'L1',
      L1: 'L2',
      L2: 'L3',
      L3: 'L4',
      L4: undefined as any
    };
    const newLevel = cycle[String(currentLevel)];
    setExperienceLevels(prev => {
      const nextContributorLevels = {
        ...(prev[contributorId] || {})
      };
      if (newLevel) {
        nextContributorLevels[solutionId] = newLevel;
      } else {
        delete nextContributorLevels[solutionId];
      }
      return {
        ...prev,
        [contributorId]: nextContributorLevels
      };
    });
    if (contributor && solution) {
      const currentStatus = availability[contributorId]?.[solutionId] || 'unavailable';
      logExperienceLevelChange(contributor, solution, newLevel, currentStatus).then(notifyDbResult);
    }
    touchContributor(contributorId);
  };
  const handleUpdateContributorRegion = (id: string, region: Region) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === id);
    const updatedContributor = contributor ? {
      ...contributor,
      region,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === id ? {
      ...c,
      region,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
  };
  const handleAddContributor = async () => {
    if (!requestAdminAccess()) return;
    let hasError = false;
    if (!newICName.trim()) {
      setNewICNameError('Name is required.');
      hasError = true;
    } else {
      setNewICNameError('');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!newICEmail.trim()) {
      setNewICEmailError('Email is required.');
      hasError = true;
    } else if (!emailRegex.test(newICEmail.trim())) {
      setNewICEmailError('Enter a valid email address.');
      hasError = true;
    } else {
      setNewICEmailError('');
    }
    if (hasError) return;
    const contributorName = newICName.trim();
    const contributorId = stableSlugId(contributorName);
    if (!contributorId) {
      setNewICNameError('Unable to generate a valid ID for that name.');
      return;
    }
    if (contributors.some(contributor => contributor.id === contributorId)) {
      setNewICNameError('A contributor with that name already exists.');
      return;
    }
    const newContributor: Contributor = {
      // id: crypto.randomUUID(),
      id: contributorId,
      name: contributorName,
      email: newICEmail.trim(),
      region: newICRegion,
      lastUpdated: new Date().toISOString()
    };
    const nextContributors = reindexContributors([...contributors, newContributor]);
    const createdContributor = nextContributors.find(c => c.id === newContributor.id) || nextContributors[nextContributors.length - 1];
    const response = await upsertContributorRecord(createdContributor);
    notifyDbResult(response);
    if (!response.success) return;
    setContributors(nextContributors);
    setNewICName('');
    setNewICEmail('');
    setNewICRegion('USA');
    setNewICNameError('');
    setNewICEmailError('');
    setIsAddICOpen(false);
  };
  const handleAddSolution = () => {
    if (!requestAdminAccess()) return;
    if (!newSolutionName.trim()) {
      setNewSolutionNameError('Solution name is required.');
      return;
    }
    const solutionName = newSolutionName.trim();
    if (solutions.some(s => s.name.toLowerCase() === solutionName.toLowerCase())) {
      setNewSolutionNameError('A solution with that name already exists.');
      return;
    }
    setNewSolutionNameError('');
    const newSolution: Solution = {
      // id: crypto.randomUUID(),
      id: stableSlugId(solutionName),
      name: solutionName
    };
    const nextSolutions = reindexSolutions([...solutions, newSolution]);
    const createdSolution = nextSolutions.find(solution => solution.id === newSolution.id) || nextSolutions[nextSolutions.length - 1];
    setSolutions(nextSolutions);
    if (createdSolution) {
      upsertSolutionRecord(createdSolution).then(notifyDbResult);
    }
    setNewSolutionName('');
    setNewSolutionNameError('');
    setIsAddSolutionOpen(false);
  };
  const handleDeleteContributor = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this contributor?')) {
      const contributor = contributors.find(c => c.id === id);
      const nextContributors = reindexContributors(contributors.filter(c => c.id !== id));
      setContributors(nextContributors);
      setAvailability(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      setExperienceLevels(prev => {
        const next = {
          ...prev
        };
        delete next[id];
        return next;
      });
      if (contributor) {
        const result = await deleteContributorRecord({
          id: contributor.id,
          name: contributor.name,
          region: contributor.region
        });
        notifyDbResult(result);
      }
      saveContributorOrder(nextContributors);
    }
  };
  const handleDeleteSolution = async (id: string) => {
    if (!requestAdminAccess()) return;
    if (confirm('Are you sure you want to remove this solution?')) {
      const solution = solutions.find(s => s.id === id);
      const nextSolutions = reindexSolutions(solutions.filter(s => s.id !== id));
      setSolutions(nextSolutions);
      setAvailability(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      setExperienceLevels(prev => Object.fromEntries(Object.entries(prev).map(([contributorId, solutionMap]) => {
        const nextSolutionMap = {
          ...solutionMap
        };
        delete nextSolutionMap[id];
        return [contributorId, nextSolutionMap];
      })));
      if (solution) {
        const result = await deleteSolutionRecord({
          id: solution.id,
          name: solution.name
        });
        notifyDbResult(result);
      }
      saveSolutionOrder(nextSolutions);
    }
  };
  // Status filter handler
  const handleStatusFilterClick = (status: AvailabilityStatus) => {
    setSelectedStatusFilter(selectedStatusFilter === status ? null : status);
  };
  // Experience level filter handler
  const handleExperienceFilterClick = (level: ExperienceLevel) => {
    setSelectedExperienceFilter(selectedExperienceFilter === level ? null : level);
  };
  // Region filter handler
  const handleRegionFilterClick = (region: Region) => {
    setSelectedRegionFilter(selectedRegionFilter === region ? null : region);
  };
  // Filter handlers
  const handleContributorClick = (contributorId: string) => {
    setWeekViewContributorId(weekViewContributorId === contributorId ? null : contributorId);
  };
  const handleCloseWeekView = () => {
    setWeekViewContributorId(null);
  };
  const handleSolutionClick = (solutionId: string) => {
    setSelectedSolutionId(selectedSolutionId === solutionId ? null : solutionId);
  };
  const handleClearFilters = () => {
    setSelectedContributorId(null);
    setSelectedSolutionId(null);
    setSelectedStatusFilter(null);
    setSelectedExperienceFilter(null);
    setSelectedRegionFilter(null);
  };
  // Filtered views - apply search AND click filters
  const filteredContributors = contributors.filter(c => c.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredSolutions = solutions.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const hasContributorMatch = filteredContributors.length > 0;
  const hasSolutionMatch = filteredSolutions.length > 0;
  let displayContributors = searchQuery === '' ? contributors : hasContributorMatch ? filteredContributors : contributors;
  let displaySolutions = searchQuery === '' ? solutions : hasSolutionMatch ? filteredSolutions : solutions;
  // Hide contributors flagged as not displayed for viewer/editor access levels;
  // admins and superadmins always see them
  if (!isAdmin) {
    displayContributors = displayContributors.filter(c => c.display !== false);
  }
  // Apply click filters
  if (selectedContributorId) {
    displayContributors = displayContributors.filter(c => c.id === selectedContributorId);
  }
  if (selectedSolutionId) {
    displaySolutions = displaySolutions.filter(s => s.id === selectedSolutionId);
  }
  // Apply region filter
  if (selectedRegionFilter) {
    displayContributors = displayContributors.filter(c => c.region === selectedRegionFilter);
  }
  // Apply status + experience filters - supports multi-select (both can be active)
  let statusFilteredContributors = displayContributors;
  let statusFilteredSolutions = displaySolutions;
  if (selectedStatusFilter || selectedExperienceFilter) {
    const contributorIdsMatching = new Set<string>();
    const solutionIdsMatching = new Set<string>();
    displayContributors.forEach(contributor => {
      displaySolutions.forEach(solution => {
        const status = availability[contributor.id]?.[solution.id] || 'unavailable';
        const expLevel = experienceLevels[contributor.id]?.[solution.id];
        const statusMatch = !selectedStatusFilter || status === selectedStatusFilter;
        const expMatch = !selectedExperienceFilter || expLevel === selectedExperienceFilter;
        if (statusMatch && expMatch) {
          contributorIdsMatching.add(contributor.id);
          solutionIdsMatching.add(solution.id);
        }
      });
    });
    statusFilteredContributors = displayContributors.filter(c => contributorIdsMatching.has(c.id));
    statusFilteredSolutions = displaySolutions.filter(s => solutionIdsMatching.has(s.id));
  }
  const selectedContributor = contributors.find(c => c.id === selectedContributorId);
  const selectedSolution = solutions.find(s => s.id === selectedSolutionId);
  const hasActiveFilters = selectedContributorId !== null || selectedSolutionId !== null || selectedStatusFilter !== null || selectedExperienceFilter !== null || selectedRegionFilter !== null;
  // Reorder handlers
  const handleReorderContributors = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newContributors = [...contributors];
    const [removed] = newContributors.splice(fromIndex, 1);
    newContributors.splice(toIndex, 0, removed);
    const reorderedContributors = reindexContributors(newContributors);
    setContributors(reorderedContributors);
    saveContributorOrder(reorderedContributors);
  };
  const handleReorderSolutions = (fromIndex: number, toIndex: number) => {
    if (!requestAdminAccess()) return;
    const newSolutions = [...solutions];
    const [removed] = newSolutions.splice(fromIndex, 1);
    newSolutions.splice(toIndex, 0, removed);
    const reorderedSolutions = reindexSolutions(newSolutions);
    setSolutions(reorderedSolutions);
    saveSolutionOrder(reorderedSolutions);
  };
  // Edit handlers
  const handleEditContributor = (contributor: Contributor) => {
    if (!requestAdminAccess()) return;
    setEditingContributor(contributor);
    setEditContributorName(contributor.name);
    setEditContributorRegion(contributor.region);
    setEditContributorDisplay(contributor.display ?? true);
    setIsEditContributorOpen(true);
  };
  const handleSaveContributor = () => {
    if (!requestAdminAccess()) return;
    if (!editingContributor || !editContributorName.trim()) return;
    const updatedContributor: Contributor = {
      ...editingContributor,
      name: editContributorName.trim(),
      region: editContributorRegion,
      display: editContributorDisplay,
      lastUpdated: new Date().toISOString()
    };
    setContributors(prev => prev.map(c => c.id === editingContributor.id ? updatedContributor : c));
    saveContributorRecord(updatedContributor);
    setIsEditContributorOpen(false);
    setEditingContributor(null);
    setEditContributorName('');
  };
  const handleEditSolution = (solution: Solution) => {
    if (!requestAdminAccess()) return;
    setEditingSolution(solution);
    setEditSolutionName(solution.name);
    setIsEditSolutionOpen(true);
  };
  const handleSaveSolution = () => {
    if (!requestAdminAccess()) return;
    if (!editingSolution || !editSolutionName.trim()) return;
    const nextSolutions = reindexSolutions(solutions.map(s => s.id === editingSolution.id ? {
      ...s,
      name: editSolutionName.trim()
    } : s));
    const updatedSolution = nextSolutions.find(s => s.id === editingSolution.id);
    setSolutions(nextSolutions);
    if (updatedSolution) {
      upsertSolutionRecord(updatedSolution).then(notifyDbResult);
    }
    setIsEditSolutionOpen(false);
    setEditingSolution(null);
    setEditSolutionName('');
  };
  // OOO handler - now opens modal instead of toggling
  const handleOpenOOOModal = (contributorId: string) => {
    if (!requestManagementAccess()) return;
    const contributor = contributors.find(c => c.id === contributorId);
    setOOOTargetContributorId(contributorId);
    setOOOStartDate(contributor?.oooStartDate || '');
    setOOOEndDate(contributor?.oooEndDate || '');
    setOOOError('');
    setIsOOOModalOpen(true);
  };
  const handleSaveOOO = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    setOOOError('');
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    // If both dates are empty, clear OOO
    if (!oooStartDate && !oooEndDate) {
      const updatedContributor = contributor ? {
        ...contributor,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined,
        lastUpdated: new Date().toISOString()
      } : null;
      setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
        ...c,
        isOutOfOffice: false,
        oooStartDate: undefined,
        oooEndDate: undefined,
        lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
      } : c));
      if (updatedContributor) {
        saveContributorRecord(updatedContributor);
      }
      setIsOOOModalOpen(false);
      return;
    }
    if (!oooStartDate || !oooEndDate) {
      setOOOError('Please select both start and end dates');
      return;
    }
    const start = new Date(oooStartDate);
    const end = new Date(oooEndDate);
    if (end < start) {
      setOOOError('End date must be on or after start date');
      return;
    }
    const today = new Date().toISOString().split('T')[0];
    const isCurrentlyOOO = oooStartDate <= today && oooEndDate >= today;
    const updatedContributor = contributor ? {
      ...contributor,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: isCurrentlyOOO,
      oooStartDate,
      oooEndDate,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
    setIsOOOModalOpen(false);
  };
  const handleClearOOO = () => {
    if (!requestManagementAccess()) return;
    if (!oooTargetContributorId) return;
    const contributor = contributors.find(c => c.id === oooTargetContributorId);
    const updatedContributor = contributor ? {
      ...contributor,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined,
      lastUpdated: new Date().toISOString()
    } : null;
    setContributors(prev => prev.map(c => c.id === oooTargetContributorId ? {
      ...c,
      isOutOfOffice: false,
      oooStartDate: undefined,
      oooEndDate: undefined,
      lastUpdated: updatedContributor?.lastUpdated || c.lastUpdated
    } : c));
    if (updatedContributor) {
      saveContributorRecord(updatedContributor);
    }
    setIsOOOModalOpen(false);
  };
  // Date Availability Handlers
  // Icon click → slide-over panel
  const handleOpenWeekView = (contributorId: string) => {
    setWeekPanelContributorId(contributorId);
  };
  const handleClosePanelView = () => {
    setWeekPanelContributorId(null);
  };
  const handleUpdateDailyAvailability = async (contributorId: string, solutionId: string, date: string, status: AvailabilityStatus | null) => {
    const contributor = contributors.find(c => c.id === contributorId);
    const solution = solutions.find(s => s.id === solutionId);
    const result = await updateDailyAvailability(contributorId, solutionId, date, status, contributor, solution);
    notifyDbResult(result);
  };
  const accessLevelLabel = accessLevel === 'superadmin' ? 'Superadmin' : accessLevel === 'admin' ? 'Admin' : accessLevel === 'editor' ? 'Editor' : accessLevel === 'viewer' ? 'Viewer' : accessLevel === 'unregistered' ? 'Unregistered' : 'Signed out';
  if (isHydrating) {
    return <div className="flex h-screen items-center justify-center bg-slate-50 text-slate-500 font-sans" data-id="element-141" data-mp="1749pvi6mtip7">
        {isAuthReady ? 'Loading resource data...' : 'Initializing Adobe sign-in...'}
      </div>;
  }
  if (!isSignedIn) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-142" data-mp="sdsp811tmwtd">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-143" data-mp="nf93a0n89l10">
          <div className="flex items-center gap-4" data-id="element-144" data-mp="2te4wf2h87i5">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-145" data-mp="2uyxhz58dgdd" />
            <div data-id="element-146" data-mp="bvx1ihfo5nvy">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-147" data-mp="gsdsrki60id9">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-148" data-mp="lqyrffhrqtzl">
                Sign in with Adobe ID
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-149" data-mp="pqz7t5ofhf9a">
            <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-150" data-mp="ul3cfia81b77">
              Dashboard data is available only after Adobe IMS login.
              <br data-id="element-151" data-mp="14irk18sxed6s" /> <br data-id="element-152" data-mp="14irhp25ge13m" />
              <p data-id="element-153" data-mp="14jauk4g8xzas">
              1. Click Sign-In <br data-id="element-154" data-mp="14g52qhg8gjbd" />
              2. Login using your company account <br data-id="element-155" data-mp="19gj8tty9xj6r" />
              3. Choose profile : Adobe Inc
              </p>
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-156" data-mp="2u0y770n4buo1">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="primary" size="lg" isLoading={isAuthLoading} data-id="element-157" data-mp="2k0e37q87sc24">
              
              <LogIn className="mr-2 h-4 w-4" data-id="element-158" data-mp="3cpl9xchto7yg" />
              Sign in with Adobe ID
            </Button>
          </div>
        </div>
      </div>;
  }
  if (accessLevel === 'unregistered') {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 py-10 text-slate-900 font-sans" data-id="element-159" data-mp="32zrx685y1msm">
        <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white p-8 shadow-sm" data-id="element-160" data-mp="2y1qevpgcdri7">
          <div className="flex items-center gap-4" data-id="element-161" data-mp="3vhe4hbcji18a">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-14 w-auto" data-id="element-162" data-mp="3vi74zukwnuhz" />
            <div data-id="element-163" data-mp="1kz6rfu2nb9st">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-164" data-mp="1kyn8ca19efd7">
                Resource Availability Dashboard
              </h1>
              <p className="mt-1 text-sm text-slate-500" data-id="element-165" data-mp="1g6vnmg4sb3uz">
                Account not provisioned
              </p>
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-166" data-mp="177idrmcr0d9q">
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-id="element-167" data-mp="178jofppuqxqx">
              You are signed in with Adobe ID, but this account is not registered for dashboard access.
              No resource data is shown until an administrator assigns you a role in the system.
            </div>

            {authError && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-168" data-mp="2rnd8diz0ri1s">
                {authError}
              </div>}

            <Button onClick={handleAuthButtonClick} variant="outline" size="lg" isLoading={isAuthLoading} data-id="element-169" data-mp="2mmfsjd1hf03g">
              
              <LogOut className="mr-2 h-4 w-4" data-id="element-170" data-mp="2z5l738uf8w9n" />
              Sign out
            </Button>
          </div>
        </div>
      </div>;
  }
  return <div className="flex flex-col h-screen bg-slate-50 text-slate-900 font-sans" data-id="element-171" data-mp="38wo8kbr9jd9g">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-6 py-4 shadow-sm z-30" data-id="element-172" data-mp="2c6yhfgi3ddky">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-173" data-mp="2g8aaxx436iyf">
          <div className="flex items-center gap-3" data-id="element-174" data-mp="2ga3fn6v52t3s">
            <img src="https://cdn.magicpatterns.com/uploads/6yi4FNzoPfGEun9849jQCu/Screenshot_2026-02-03_at_7.33.48_PM.png" alt="Adobe OnDemand" className="h-12 w-auto" data-id="element-175" data-mp="2l6lj83eqglej" />
            
            <div data-id="element-176" data-mp="gw7hz9btfq2">
              <h1 className="text-xl font-bold text-slate-900" data-id="element-177" data-mp="ji9ljdg730w">
                Resource Availability Dashboard
              </h1>
              <div className="flex items-center gap-2" data-id="element-178" data-mp="3bwy2ll9hlmzm">
                <p className="text-sm text-slate-500" data-id="element-179" data-mp="3bzkbppc10ajy">
                  Track solution coverage and individual capacity
                </p>
                {dbFeedback && <div className={`flex items-center gap-1 text-xs animate-in fade-in duration-200 ${dbFeedback.type === 'success' ? 'text-green-600' : 'text-red-600'}`} data-id="element-180" data-mp="3lv59tcdz4qof">
                    {dbFeedback.type === 'success' ? <Check className="h-3 w-3" data-id="element-181" data-mp="2o8odoagz8j49" /> : <AlertCircle className="h-3 w-3" data-id="element-182" data-mp="2o5sgwpgv80he" />}
                    <span data-id="element-183" data-mp="32xul2lpyxfn8">{dbFeedback.message}</span>
                  </div>}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap" data-id="element-184" data-mp="2y3zwp4zf4cht">
            <div className="relative hidden md:block" data-id="element-185" data-mp="2af75fltqqstj">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-186" data-mp="25hng9qvi8vmt" />
              <input type="text" placeholder="Search ICs or solutions..." className="pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] w-64" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-187" data-mp="kzmp1rem7mtg" />
              
            </div>
            <div className="h-8 w-px bg-slate-200 mx-2 hidden md:block" data-id="element-188" data-mp="fxxa40o9up03" />

            {isSignedIn && <div className="hidden lg:flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-600" data-id="element-189" data-mp="18oxi0mpq4cx1">
                <span className={`h-2 w-2 rounded-full ${canManageData ? 'bg-green-500' : 'bg-amber-500'}`} data-id="element-190" data-mp="uoebjk0wpewz" />
                <span className="font-medium text-slate-800" data-id="element-191" data-mp="yoyc6xkov0nv">
                  {user?.displayName || 'Adobe user'}
                </span>
                {user?.email && <span className="text-slate-500" data-id="element-192" data-mp="3ji1smgm8ermy">{user.email}</span>}
                <span className={`rounded-full px-2 py-0.5 font-medium ${accessLevel === 'superadmin' || accessLevel === 'admin' ? 'bg-slate-900 text-white' : accessLevel === 'editor' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-193" data-mp="3j0kfpfz6no0">
                  
                  {accessLevelLabel}
                </span>
              </div>}

            <Button onClick={handleAuthButtonClick} variant={isSignedIn ? 'outline' : 'primary'} size="sm" isLoading={isAuthLoading && !isSignedIn} data-id="element-194" data-mp="mhcvjgqg67m8">
              
              {isSignedIn ? <LogOut className="h-4 w-4 mr-2" data-id="element-195" data-mp="chugnepe9c11" /> : <LogIn className="h-4 w-4 mr-2" data-id="element-196" data-mp="8i3r1eblt1y3" />}
              {isSignedIn ? 'Sign Out' : 'Sign in with Adobe ID'}
            </Button>

            {isAdmin && <>
                <Button onClick={handleDownloadCurrentData} variant="outline" size="sm" data-id="element-197" data-mp="2t8azeg7l4nv0">

                  <Download className="h-4 w-4 mr-2" data-id="element-198" data-mp="18rbkeojg67ky" />
                  Export Data
                </Button>
                <Button onClick={handleDownloadAuditLog} variant="outline" size="sm" isLoading={isExportingLog} data-id="element-199" data-mp="1cs58bhww3mdu">

                  <ScrollText className="h-4 w-4 mr-2" data-id="element-200" data-mp="1migoq3xrfo1m" />
                  Log
                </Button>
                {/* <Button
                 onClick={() => setIsImportOpen(true)}
                 variant="outline"
                 size="sm">
                                   <Upload className="h-4 w-4 mr-2" />
                  Import
                 </Button> */}
                <Button onClick={() => setIsAddICOpen(true)} variant="outline" size="sm" data-id="element-201" data-mp="25g114bjc8u5c">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-202" data-mp="64zy5lai1egs" />
                  Add Contributor
                </Button>
                <Button onClick={() => setIsAddSolutionOpen(true)} variant="primary" size="sm" data-id="element-203" data-mp="b4cju2pmwfhs">
                
                  <Plus className="h-4 w-4 mr-2" data-id="element-204" data-mp="19f4b97vf5z60" />
                  Add Solution
                </Button>
              </>}
          </div>
        </div>

        <div className="flex justify-end" data-id="element-205" data-mp="1ier6fbxk28qm">
          <span className="text-xs hover:text-blue-500" data-id="element-206" data-mp="1idzjepi0xjby"><a target="_blank" href="https://adobe-my.sharepoint.com/:x:/r/personal/dsagar_adobe_com/_layouts/15/doc2.aspx?sourcedoc=%7B430e4f3f-47f7-428f-9fb3-2c6f5f88ee7d%7D&action=edit" data-id="element-207" data-mp="1xc1oj44pecvb">Feedback</a></span><span className="text-xs ml-1 italic" data-id="element-208" data-mp="1sb45kpbvdesr">v2.0</span>
        </div>
        {/* Mobile Search */}
        <div className="mt-4 md:hidden relative" data-id="element-209" data-mp="222qf8h8k75te">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-210" data-mp="2241jei5x2fl1" />
          <input type="text" placeholder="Search ICs or solutions..." className="w-full pl-9 pr-4 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000]" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-211" data-mp="2g3kmuxns92r0" />
          
        </div>

        {(authError || isAuthReady && !isSignedIn || isSignedIn && !canManageData || isSignedIn && canManageData && !isAdmin) && <div className="mt-4 space-y-2" data-id="element-212" data-mp="2ppc64sgl4vyj">
            {authError && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-id="element-213" data-mp="2byqnn4v1mo16">
                {authError}
              </div>}
            {isAuthReady && !isSignedIn && !authError && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800" data-id="element-214" data-mp="2212p03ubgwdq">
                Sign in with Adobe ID to unlock
              </div>}
            {isSignedIn && !canManageData && accessLevel === 'viewer' && <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-id="element-215" data-mp="1t1z6b0z2aizr">
                Signed in as{' '}
                <span className="font-medium" data-id="element-216" data-mp="2dh8qw8c7lclt">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                This account has viewer access only.
              </div>}
            {isSignedIn && canManageData && !isAdmin && <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700" data-id="element-217" data-mp="1yjq0z2nsotcs">
                Signed in as{' '}
                <span className="font-medium" data-id="element-218" data-mp="1uiwfefxtrqn2">
                  {user?.email || user?.displayName || 'Adobe user'}
                </span>.
                
              </div>}
          </div>}
      </header>

      {/* Legend & Active Filters */}
      <div className="bg-white border-b border-slate-200 px-6 py-2" data-id="element-219" data-mp="1ftgo8yudtj6u">
        <div className="flex flex-col gap-2" data-id="element-220" data-mp="1frdrlvyq4rt5">
          <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-221" data-mp="1audo9st9e35q">
            <div className="flex items-center gap-4 text-xs" data-id="element-222" data-mp="16ss9u312tgtf">
              <span className="font-medium text-slate-600" data-id="element-223" data-mp="16umynsfgxt2s">Status:</span>
              {/* Clickable status filters */}
              <button onClick={() => handleStatusFilterClick('available')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'available' ? 'bg-green-100 ring-2 ring-green-500 ring-offset-1' : 'hover:bg-green-50'}`} title="Click to filter by Qualified and Available" data-id="element-224" data-mp="3mhh46wuc6jtu">
                
                <div className="h-3 w-3 rounded-full bg-green-500" data-id="element-225" data-mp="33qzdext2dyb4"></div>
                <span className={selectedStatusFilter === 'available' ? 'text-green-700 font-medium' : 'text-slate-700'} data-id="element-226" data-mp="33r946nlxo9hv">
                  
                  Qualified and Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('limited')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'limited' ? 'bg-yellow-100 ring-2 ring-yellow-500 ring-offset-1' : 'hover:bg-yellow-50'}`} title="Click to filter by Qualified not Available" data-id="element-227" data-mp="2fzkhbmrvsd8b">
                
                <div className="h-3 w-3 rounded-full bg-yellow-400" data-id="element-228" data-mp="1ls8s2gm59toc"></div>
                <span className={selectedStatusFilter === 'limited' ? 'text-yellow-700 font-medium' : 'text-slate-700'} data-id="element-229" data-mp="1ppwue4q62oq9">
                  
                  Qualified not Available
                </span>
              </button>
              <button onClick={() => handleStatusFilterClick('unavailable')} className={`flex items-center gap-2 px-2 py-1 rounded-md transition-all cursor-pointer ${selectedStatusFilter === 'unavailable' ? 'bg-red-100 ring-2 ring-red-500 ring-offset-1' : 'hover:bg-red-50'}`} title="Click to filter by Not Qualified" data-id="element-230" data-mp="11zu51wdmzwvc">
                
                <div className="h-3 w-3 rounded-full bg-red-500" data-id="element-231" data-mp="ees1twtljk20"></div>
                <span className={selectedStatusFilter === 'unavailable' ? 'text-red-700 font-medium' : 'text-slate-700'} data-id="element-232" data-mp="9ggsrob4ldku">
                  
                  Not Qualified
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-233" data-mp="xc2yrarra1ea" />

              <span className="font-medium text-slate-600" data-id="element-234" data-mp="set3vfllkxj2">Level:</span>
              {/* Clickable experience level filters */}
              <button onClick={() => handleExperienceFilterClick('L1')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L1' ? 'bg-slate-200 ring-2 ring-slate-400 ring-offset-1' : 'hover:bg-slate-50'}`} title="Click to filter by L1 - Foundational" data-id="element-235" data-mp="171w0lk23j25c">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-200" data-id="element-236" data-mp="8so0d0ji9dxo">
                  L1
                </span>
                <span className={selectedExperienceFilter === 'L1' ? 'text-slate-800 font-medium' : 'text-slate-700'} data-id="element-237" data-mp="hrj6ydqgoq1i">
                  
                  Foundational
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-238" data-mp="wgp4zydkca7t">
                  FOUNDATIONAL

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-239" data-mp="15faukdhgcpi3">Enablement Requirements:</p>
                  <p data-id="element-240" data-mp="339rmw8mbd5ul">Intro-level enablement completed with understanding of the basics and value of the solution.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-241" data-mp="2zp7mlvrtc2jd">Tooling Expectations:</p>
                  <p data-id="element-242" data-mp="2uyr5o1os9ys9">Learners can do a standard demo in a clickable and shared environment.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-243" data-mp="2pzo44r80iap1">Field Support Implications:</p>
                  <p data-id="element-244" data-mp="2ly2qh3nnlsds">Learners are apprenticing in the field and can support 1:Many demos.</p>

                  <p className="text-m font-boltext-m font-bold mt-2" data-id="element-245" data-mp="3iod58q92ig0s">Outcome:</p>
                  <p data-id="element-246" data-mp="3im92ibmwnn23">Learners should be able to deliver a value pitch and 101 level demo.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L2')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L2' ? 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1' : 'hover:bg-blue-50'}`} title="Click to filter by L2 - Intermediate" data-id="element-247" data-mp="39n5jf7nqn7nl">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-blue-50 text-blue-600 border-blue-200" data-id="element-248" data-mp="3h5l8vqbk9468">
                  L2
                </span>
                <span className={selectedExperienceFilter === 'L2' ? 'text-blue-700 font-medium' : 'text-slate-700'} data-id="element-249" data-mp="3lyo7wdz3x5tt">
                  
                  Intermediate
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-250" data-mp="3s002gcoa4kg">
                  INTERMEDIATE

                  <p className="font-bold mt-2" data-id="element-251" data-mp="3vtg3jc94pily">Enablement Requirements:</p>
                  <p data-id="element-252" data-mp="2fghrx7gb8byd">Deeper dives into features, customer use Requests, and common integrations.</p>

                  <p className="font-bold mt-2" data-id="element-253" data-mp="2kgvsk4fl0jmd">Tooling Expectations:</p>
                  <p data-id="element-254" data-mp="2byk3h6fqgab6">Learners can do an ad hoc demo in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-255" data-mp="2c0ohih7cipp6">Field Support Implications:</p>
                  <p data-id="element-256" data-mp="27wyv9ye58k7b">Learners are supporting early-stage or low complexity single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-257" data-mp="2lux30kxylidu">Outcome:</p>
                  <p data-id="element-258" data-mp="2gz83ghlxw1hj">Learners have completed numerous customer demos that show practical application. They should be doing ad hoc demos where they’re able to go off script.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L3')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L3' ? 'bg-indigo-100 ring-2 ring-indigo-400 ring-offset-1' : 'hover:bg-indigo-50'}`} title="Click to filter by L3 - Advanced" data-id="element-259" data-mp="1ob2hjrj5g6g4">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-indigo-50 text-indigo-600 border-indigo-200" data-id="element-260" data-mp="15ihs6k2nsjh2">
                  L3
                </span>
                <span className={selectedExperienceFilter === 'L3' ? 'text-indigo-700 font-medium' : 'text-slate-700'} data-id="element-261" data-mp="3aj2fisf3lemz">
                  
                  Advanced
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-262" data-mp="2mmwxg5vi0wnf">
                  ADVANCED

                  <p className="font-bold mt-2" data-id="element-263" data-mp="30lo8fjf2e27c">Enablement Requirements:</p>
                  <p data-id="element-264" data-mp="30jtmc1s2441o">Covers integrations with other solutions, data migration architectures, and expert-level guidance.</p>

                  <p className="font-bold mt-2" data-id="element-265" data-mp="fvfavngvznzk">Tooling Expectations:</p>
                  <p data-id="element-266" data-mp="fxs2kscg5cpt">Learners can demo product integrations and add-ons in a live environment.</p>

                  <p className="font-bold mt-2" data-id="element-267" data-mp="q1qahrtwir5v">Field Support Implications:</p>
                  <p data-id="element-268" data-mp="l12mzg4vssge">Learners are supporting all single solution customer meetings.</p>

                  <p className="font-bold mt-2" data-id="element-269" data-mp="g8hvil9g6rg1">Outcome:</p>
                  <p data-id="element-270" data-mp="c8h4m155wns3">Learners can easily lead ad hoc demos and go off script live with a customer. Learners have a comprehensive understanding of the tool and multiple closed deals to show practical application.</p>
                </span>
              </button>
              <button onClick={() => handleExperienceFilterClick('L4')} className={`relative flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer group ${selectedExperienceFilter === 'L4' ? 'bg-violet-100 ring-2 ring-violet-400 ring-offset-1' : 'hover:bg-violet-50'}`} title="Click to filter by L4 - Champion" data-id="element-271" data-mp="28qc2505wiun">
                
                <span className="text-[9px] font-bold px-1 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-200" data-id="element-272" data-mp="1no5xf953kh0t">
                  L4
                </span>
                <span className={selectedExperienceFilter === 'L4' ? 'text-violet-700 font-medium' : 'text-slate-700'} data-id="element-273" data-mp="2awfwryiv0uy1">
                  
                  Champion
                </span>
                <span className="z-30 pointer-events-none absolute left-1/2 top-full z-20 mt-2 pb-4 hidden w-56 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-left text-xs font-normal leading-relaxed text-white shadow-lg group-hover:block" data-id="element-274" data-mp="1n7vjyboyeon2">
                  CHAMPION

                  <p className="font-bold mt-2" data-id="element-275" data-mp="1j6a026e05a3d">Field Support Implications:</p>
                  <p data-id="element-276" data-mp="1j8ee3h5m7phd">Learners can support architecture and multi-solution conversations. The most complex meetings.</p>

                  <p className="font-bold mt-2" data-id="element-277" data-mp="3dkay4p1q9t2e">Outcome:</p>
                  <p data-id="element-278" data-mp="3djt47z2jfqxn">Learners are the SME on the team for an individual product and a thought leader as the product evolves.</p>
                </span>
              </button>

              <div className="h-4 w-px bg-slate-200 mx-1" data-id="element-279" data-mp="3iqpuwxhs7auq" />

              <span className="font-medium text-slate-600" data-id="element-280" data-mp="b6xsevmyq95">Region:</span>
              {REGIONS.map(region => {
              const isActive = selectedRegionFilter === region;
              const colorMap: Record<Region, {
                active: string;
                hover: string;
                text: string;
                dot: string;
              }> = {
                USA: {
                  active: 'bg-blue-100 ring-2 ring-blue-400 ring-offset-1',
                  hover: 'hover:bg-blue-50',
                  text: 'text-blue-700 font-medium',
                  dot: 'bg-blue-500'
                },
                LATAM: {
                  active: 'bg-amber-100 ring-2 ring-amber-400 ring-offset-1',
                  hover: 'hover:bg-amber-50',
                  text: 'text-amber-700 font-medium',
                  dot: 'bg-amber-500'
                },
                India: {
                  active: 'bg-emerald-100 ring-2 ring-emerald-400 ring-offset-1',
                  hover: 'hover:bg-emerald-50',
                  text: 'text-emerald-700 font-medium',
                  dot: 'bg-emerald-500'
                },
                EMEA: {
                  active: 'bg-purple-100 ring-2 ring-purple-400 ring-offset-1',
                  hover: 'hover:bg-purple-50',
                  text: 'text-purple-700 font-medium',
                  dot: 'bg-purple-500'
                }
              };
              const colors = colorMap[region];
              return <button key={region} onClick={() => handleRegionFilterClick(region)} className={`flex items-center gap-1.5 px-2 py-1 rounded-md transition-all cursor-pointer ${isActive ? colors.active : colors.hover}`} title={`Click to filter by ${region}`} data-id="element-281" data-mp="2pwy079enraoe">
                    
                    <div className={`h-2.5 w-2.5 rounded-sm ${colors.dot}`} data-id="element-282" data-mp="2jg4u1n2bws1d" />
                    <span className={isActive ? colors.text : 'text-slate-700'} data-id="element-283" data-mp="2od4qr7dagift">
                      {region}
                    </span>
                  </button>;
            })}
            </div>
          </div>

          {/* Active Filters */}
          {hasActiveFilters && <div className="flex items-center gap-2" data-id="element-284" data-mp="32dyzjr490lf0">
              <span className="text-xs font-medium text-slate-600" data-id="element-285" data-mp="32d5zta3piw6d">
                Filtered:
              </span>
              {selectedStatusFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${STATUS_COLORS[selectedStatusFilter].bg} ${STATUS_COLORS[selectedStatusFilter].text}`} data-id="element-286" data-mp="3v1uvgtlzu9aq">
              
                  {STATUS_LABELS[selectedStatusFilter]}
                  <button onClick={() => setSelectedStatusFilter(null)} className={`hover:${STATUS_COLORS[selectedStatusFilter].bg} rounded-full p-0.5`} data-id="element-287" data-mp="dhl58kahbgyq">
                
                    <X className="h-3 w-3" data-id="element-288" data-mp="4ku5nxwpxjze" />
                  </button>
                </span>}
              {selectedExperienceFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedExperienceFilter === 'L1' ? 'bg-slate-100 text-slate-700' : selectedExperienceFilter === 'L2' ? 'bg-blue-100 text-blue-700' : selectedExperienceFilter === 'L3' ? 'bg-indigo-100 text-indigo-700' : 'bg-violet-100 text-violet-700'}`} data-id="element-289" data-mp="3qr95u3obv9fe">
              
                  {selectedExperienceFilter} –{' '}
                  {EXPERIENCE_LABELS[selectedExperienceFilter]}
                  <button onClick={() => setSelectedExperienceFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-290" data-mp="3303f2tgumwqb">
                
                    <X className="h-3 w-3" data-id="element-291" data-mp="1ikypasb0muq5" />
                  </button>
                </span>}
              {selectedRegionFilter && <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs ${selectedRegionFilter === 'USA' ? 'bg-blue-100 text-blue-700' : selectedRegionFilter === 'LATAM' ? 'bg-amber-100 text-amber-700' : selectedRegionFilter === 'India' ? 'bg-emerald-100 text-emerald-700' : 'bg-purple-100 text-purple-700'}`} data-id="element-292" data-mp="1shtjx0464az5">
              
                  {selectedRegionFilter}
                  <button onClick={() => setSelectedRegionFilter(null)} className="hover:bg-white/50 rounded-full p-0.5" data-id="element-293" data-mp="18rq5n8fma6tz">
                
                    <X className="h-3 w-3" data-id="element-294" data-mp="h1v2a7uxcgmb" />
                  </button>
                </span>}
              {(selectedStatusFilter || selectedExperienceFilter || selectedRegionFilter) && [selectedStatusFilter, selectedExperienceFilter, selectedRegionFilter].filter(Boolean).length > 1 && <span className="text-[10px] text-slate-400 italic" data-id="element-295" data-mp="boem2rhy7vop">
                    (combined filters)
                  </span>}
              {selectedContributor && <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs" data-id="element-296" data-mp="6vlgk1enojbk">
                  {selectedContributor.name}
                  <button onClick={() => setSelectedContributorId(null)} className="hover:bg-blue-200 rounded-full p-0.5" data-id="element-297" data-mp="foyl60m319dh">
                
                    <X className="h-3 w-3" data-id="element-298" data-mp="pnmorudbllxn" />
                  </button>
                </span>}
              {selectedSolution && <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" data-id="element-299" data-mp="2wf5o27cxzufh">
                  {selectedSolution.name}
                  <button onClick={() => setSelectedSolutionId(null)} className="hover:bg-purple-200 rounded-full p-0.5" data-id="element-300" data-mp="16wyqrmp1cwki">
                
                    <X className="h-3 w-3" data-id="element-301" data-mp="1byedrkarxd5j" />
                  </button>
                </span>}
              <button onClick={handleClearFilters} className="text-xs text-slate-500 hover:text-slate-700 underline" data-id="element-302" data-mp="126s0zjisf60o">
              
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 overflow-hidden p-4 md:p-6" data-id="element-303" data-mp="s69cgbga31cf">
        {weekViewContributorId ? (() => {
        const weekContributor = contributors.find(c => c.id === weekViewContributorId);
        if (!weekContributor) return null;
        return <div className="h-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden flex flex-col" data-id="element-304" data-mp="28o5fo4lea2ld">
              <ContributorWeekView contributor={weekContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleCloseWeekView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(weekContributor.id, sId, date, status)} showTabs={false} data-id="element-305" data-mp="2dl5k6b2lixad" />
            </div>;
      })() : <AvailabilityGrid contributors={statusFilteredContributors} solutions={statusFilteredSolutions} availability={availability} onToggleAvailability={handleToggleAvailability} onDeleteContributor={handleDeleteContributor} onDeleteSolution={handleDeleteSolution} onUpdateContributorRegion={handleUpdateContributorRegion} onContributorClick={handleContributorClick} onSolutionClick={handleSolutionClick} selectedContributorId={selectedContributorId} selectedSolutionId={selectedSolutionId} selectedStatusFilter={selectedStatusFilter} selectedExperienceFilter={selectedExperienceFilter} onReorderContributors={handleReorderContributors} onReorderSolutions={handleReorderSolutions} isAdminAuthenticated={canManageData} canAdministerEntities={isAdmin} canDeleteEntities={isAdmin} allContributors={contributors} allSolutions={solutions} onEditContributor={handleEditContributor} onEditSolution={handleEditSolution} onToggleOutOfOffice={handleOpenOOOModal} experienceLevels={experienceLevels} onCycleExperienceLevel={handleCycleExperienceLevel} onOpenWeekView={hasDashboardDataAccess ? handleOpenWeekView : undefined} data-id="element-306" data-mp="2pyiti3lya3r0" />}
      </main>

      {/* Weekly schedule slide-over panel (icon click) */}
      {weekPanelContributorId && (() => {
      const panelContributor = contributors.find(c => c.id === weekPanelContributorId);
      if (!panelContributor) return null;
      return <div className="fixed inset-y-0 right-0 z-40 flex" data-id="element-307" data-mp="1gz9egqgtfu1k">
            <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={handleClosePanelView} data-id="element-308" data-mp="1h02ez9p6lnb9" />
            <div className="relative ml-auto w-full max-w-3xl bg-white shadow-xl flex flex-col border-l border-slate-200" data-id="element-309" data-mp="p8d4bxm543hz">
              <ContributorWeekView contributor={panelContributor} solutions={solutions} baseAvailability={availability} canEdit={canManageData} onClose={handleClosePanelView} fetchCurrentWeek={fetchCurrentWeekSchedule} fetchNextWeek={fetchNextWeekSchedule} onUpdateDailyAvailability={(sId, date, status) => handleUpdateDailyAvailability(panelContributor.id, sId, date, status)} data-id="element-310" data-mp="130jjbgdk44fu" />
            </div>
          </div>;
    })()}

      {/* Import Modal */}
      <Modal isOpen={isImportOpen} onClose={() => {
      setIsImportOpen(false);
      setImportError('');
    }} title="Import Availability Data" footer={<div className="flex justify-between items-center w-full" data-id="element-312" data-mp="30vyddpel2i9s">
            <div className="flex gap-2" data-id="element-313" data-mp="34xbnwcsmf9n1">
              <Button variant="outline" onClick={handleDownloadTemplate} size="sm" data-id="element-314" data-mp="34w8t3ts6gn1u">
              
                <FileSpreadsheet className="h-4 w-4 mr-2" data-id="element-315" data-mp="2vvu8kwn1fbze" />
                Blank Template
              </Button>
              <Button variant="outline" onClick={handleDownloadCurrentData} size="sm" data-id="element-316" data-mp="1fhu9tozxhsdq">
              
                <Download className="h-4 w-4 mr-2" data-id="element-317" data-mp="1kf3wwxzuhrye" />
                Current Data
              </Button>
            </div>
            <Button variant="ghost" onClick={() => setIsImportOpen(false)} data-id="element-318" data-mp="1xx5dgb47d6qa">
              Close
            </Button>
          </div>} data-id="element-311" data-mp="288ccwq0njtr5">
        
        <div className="space-y-4" data-id="element-319" data-mp="22xjlht5t0gob">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-320" data-mp="22udxyictpmup">
            <h4 className="font-medium text-blue-900 text-sm mb-2" data-id="element-321" data-mp="27ujtwaq4lvil">
              CSV Format Instructions
            </h4>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside" data-id="element-322" data-mp="1a5q7m8d7w7aa">
              <li data-id="element-323" data-mp="1f4jli1wf20az">
                First column: <strong data-id="element-324" data-mp="1k60j9ojlt6nc">Contributor</strong> (name)
              </li>
              <li data-id="element-325" data-mp="1k3nvgur6022d">
                Second column: <strong data-id="element-326" data-mp="1owz32y3swmvc">Region</strong> (USA, LATAM, India, or
                EMEA)
              </li>
              <li data-id="element-327" data-mp="rhl9phj8rgje">Remaining columns: Solution names</li>
              <li data-id="element-328" data-mp="rez1rgsfuz0n">
                Cell format:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-329" data-mp="wfcy41kogkiu">status</code> or{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-330" data-mp="39q604stixvyh">status:level</code>
              </li>
              <li data-id="element-331" data-mp="35p40iavp3hvz">
                Status values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-332" data-mp="35pm3rrgrywde">available</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-333" data-mp="30og9hp7gv11r">limited</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-334" data-mp="2vrg3tfej40b6">unavailable</code>
              </li>
              <li data-id="element-335" data-mp="2qs3e8mw9wf4w">
                Level values:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-336" data-mp="2qtoduqxg2o9m">L1</code>{' '}
                (Foundational),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-337" data-mp="2lz0koerhx5op">L2</code>{' '}
                (Intermediate),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-338" data-mp="2d05hzcnnudq5">L3</code> (Advanced),{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-339" data-mp="2cyc5hgqd8za8">L4</code> (Champion)
              </li>
              <li data-id="element-340" data-mp="282wvizvhbnj3">
                Examples:{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-341" data-mp="2330pyw74vo1f">available:L3</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-342" data-mp="1tdu1cqagm05o">limited:L1</code>,{' '}
                <code className="bg-blue-100 px-1 rounded" data-id="element-343" data-mp="1yd6qjhoszjbf">unavailable</code>
              </li>
              <li data-id="element-344" data-mp="1kdoz0uk4hryd">
                Leave cells empty to default to "unavailable" with no level
              </li>
            </ul>
          </div>

          {importError && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-id="element-345" data-mp="1ohd4n7p72dgz">
              {importError}
            </div>}

          <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-slate-400 transition-colors" data-id="element-346" data-mp="2h47nk06d54sc">
            <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileUpload} className="hidden" id="csv-upload" data-id="element-347" data-mp="23cazq8bq9h1r" />
            
            <label htmlFor="csv-upload" className="cursor-pointer" data-id="element-348" data-mp="2zufku1ndt10m">
              <Upload className="h-12 w-12 mx-auto text-slate-400 mb-3" data-id="element-349" data-mp="2zvqp02kqoas9" />
              <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-350" data-mp="oef4yb7cx7h1">
                Click to upload CSV file
              </p>
              <p className="text-xs text-slate-500" data-id="element-351" data-mp="jg3v40h2awyt">or drag and drop</p>
            </label>
          </div>

          <p className="text-xs text-slate-500" data-id="element-352" data-mp="y7co8jk6b0gp">
            <strong data-id="element-353" data-mp="t5x54x1k340y">Note:</strong> Importing will replace all current data.
            Download your current data first if you want to keep it.
          </p>
        </div>
      </Modal>

      {/* Add Contributor Modal */}
      <Modal isOpen={isAddICOpen} onClose={() => {
      setIsAddICOpen(false);
      setNewICNameError('');
      setNewICEmailError('');
    }} title="Add New Contributor" footer={<div className="flex justify-end gap-3" data-id="element-355" data-mp="aew6yb89fcig">
            <Button variant="ghost" onClick={() => setIsAddICOpen(false)} data-id="element-356" data-mp="1qtxethstr8j7">
              Cancel
            </Button>
            <Button onClick={handleAddContributor} data-id="element-357" data-mp="1lvds3f6epsps">Add Contributor</Button>
          </div>} data-id="element-354" data-mp="it4f49hxbuz">
        
        <div className="space-y-4" data-id="element-358" data-mp="3rjwm8fjzq4ew">
          <p className="text-sm text-slate-500" data-id="element-359" data-mp="3rkpob38085qp">
            Enter the name and region of the individual contributor you would
            like to add to the dashboard.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={newICName} onChange={e => {
          setNewICName(e.target.value);
          setNewICNameError('');
        }} error={newICNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-360" data-mp="4dnjqjpf5gym" />

          <Input label="Email" placeholder="e.g. jane.doe@adobe.com" type="email" value={newICEmail} onChange={e => {
          setNewICEmail(e.target.value);
          setNewICEmailError('');
        }} error={newICEmailError} onKeyDown={e => e.key === 'Enter' && handleAddContributor()} data-id="element-361" data-mp="j9cwrscmmjr7" />

          <div data-id="element-362" data-mp="1238pwzmkbehd">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-363" data-mp="122p61ddcqg0p">
              Region
            </label>
            <select value={newICRegion} onChange={e => setNewICRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-364" data-mp="1c41busqoq75x">
              
              {REGIONS.map(region => <option key={region} value={region} data-id="element-365" data-mp="1uvakz0wjmtwz">
                  {region}
                </option>)}
            </select>
          </div>
        </div>
      </Modal>

      {/* Add Solution Modal */}
      <Modal isOpen={isAddSolutionOpen} onClose={() => {
      setIsAddSolutionOpen(false);
      setNewSolutionNameError('');
    }} title="Add New Solution" footer={<div className="flex justify-end gap-3" data-id="element-367" data-mp="2dnwpsapfhutp">
            <Button variant="ghost" onClick={() => setIsAddSolutionOpen(false)} data-id="element-368" data-mp="28weo1kfkzq7o">
              Cancel
            </Button>
            <Button onClick={handleAddSolution} data-id="element-369" data-mp="6ykwpma52a2x">Add Solution</Button>
          </div>} data-id="element-366" data-mp="1l598l6djyzxw">
        
        <div className="space-y-4" data-id="element-370" data-mp="by79jum21ob7">
          <p className="text-sm text-slate-500" data-id="element-371" data-mp="bx5yhq51h1th">
            Enter the name of the new Adobe solution or product area.
          </p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={newSolutionName} onChange={e => {
          setNewSolutionName(e.target.value);
          setNewSolutionNameError('');
        }} error={newSolutionNameError} autoFocus onKeyDown={e => e.key === 'Enter' && handleAddSolution()} data-id="element-372" data-mp="284hpxqo6geg" />
          
        </div>
      </Modal>

      {/* Edit Contributor Modal */}
      <Modal isOpen={isEditContributorOpen} onClose={() => {
      setIsEditContributorOpen(false);
      setEditingContributor(null);
      setEditContributorName('');
    }} title="Edit Contributor" footer={<div className="flex justify-end gap-3" data-id="element-374" data-mp="2y52cec4hqgyp">
            <Button variant="ghost" onClick={() => {
        setIsEditContributorOpen(false);
        setEditingContributor(null);
        setEditContributorName('');
      }} data-id="element-375" data-mp="2y5vev0wf2kb1">

              Cancel
            </Button>
            <Button onClick={handleSaveContributor} data-id="element-376" data-mp="33454yx3aaups">Save Changes</Button>
          </div>} data-id="element-373" data-mp="3fffreegs8o5x">

        <div className="space-y-4" data-id="element-377" data-mp="3gv1xknx7nbwu">
          <p className="text-sm text-slate-500" data-id="element-378" data-mp="3gu0bloenp50c">
            Update the contributor's name and region.
          </p>
          <Input label="Full Name" placeholder="e.g. Jane Doe" value={editContributorName} onChange={e => setEditContributorName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveContributor()} data-id="element-379" data-mp="3cwkovzy55tdb" />

          <div data-id="element-380" data-mp="1wht86kacyaju">
            <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-381" data-mp="1rgvi7lj3wc7s">
              Region
            </label>
            <select value={editContributorRegion} onChange={e => setEditContributorRegion(e.target.value as Region)} className="flex h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EB1000] focus:border-transparent" data-id="element-382" data-mp="15o97k4g202ba">

              {REGIONS.map(region => <option key={region} value={region} data-id="element-383" data-mp="mpn8sw80enaj">
                  {region}
                </option>)}
            </select>
          </div>

          <div className="flex items-center justify-between" data-id="element-384" data-mp="rq189pvjoowy">
            <div data-id="element-385" data-mp="wm8e7inxy0ew">
              <label className="block text-sm font-medium text-slate-700" data-id="element-386" data-mp="wlq8zwjawbuu">
                Display
              </label>
              <p className="text-xs text-slate-500" data-id="element-387" data-mp="11jrsujojwf7d">
                Hide this contributor from viewers and editors. Admins and
                superadmins will still see them.
              </p>
            </div>
            <button type="button" role="switch" aria-checked={editContributorDisplay} onClick={() => setEditContributorDisplay(prev => !prev)} className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${editContributorDisplay ? 'bg-[#EB1000]' : 'bg-slate-300'}`} data-id="element-388" data-mp="1dy1gglglgrf">

              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editContributorDisplay ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-389" data-mp="3dv342qzggvbx" />

            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Solution Modal */}
      <Modal isOpen={isEditSolutionOpen} onClose={() => {
      setIsEditSolutionOpen(false);
      setEditingSolution(null);
      setEditSolutionName('');
    }} title="Edit Solution" footer={<div className="flex justify-end gap-3" data-id="element-391" data-mp="y241srbclmzw">
            <Button variant="ghost" onClick={() => {
        setIsEditSolutionOpen(false);
        setEditingSolution(null);
        setEditSolutionName('');
      }} data-id="element-392" data-mp="2zgamr1r00qa1">
            
              Cancel
            </Button>
            <Button onClick={handleSaveSolution} data-id="element-393" data-mp="2lomfpcvlwrzx">Save Changes</Button>
          </div>} data-id="element-390" data-mp="2zwd6794tarmi">
        
        <div className="space-y-4" data-id="element-394" data-mp="3iemymv9aoe9b">
          <p className="text-sm text-slate-500" data-id="element-395" data-mp="3idvag5i11it2">Update the solution name.</p>
          <Input label="Solution Name" placeholder="e.g. Adobe Firefly Video" value={editSolutionName} onChange={e => setEditSolutionName(e.target.value)} autoFocus onKeyDown={e => e.key === 'Enter' && handleSaveSolution()} data-id="element-396" data-mp="3ecs181uhil0t" />
          
        </div>
      </Modal>

      {/* OOO Date Range Modal */}
      <Modal isOpen={isOOOModalOpen} onClose={() => setIsOOOModalOpen(false)} title="Set Out of Office" footer={<div className="flex justify-between w-full" data-id="element-398" data-mp="1ixcu1mpvrlf9">
            {oooStartDate || oooEndDate ? <Button variant="danger" size="sm" onClick={handleClearOOO} data-id="element-399" data-mp="1nw65zapir4db">
                Clear OOO
              </Button> : <div data-id="element-400" data-mp="1sswn81d2860b" />}
            <div className="flex gap-3" data-id="element-401" data-mp="1xkxxjhqo3mnp">
              <Button variant="ghost" onClick={() => setIsOOOModalOpen(false)} data-id="element-402" data-mp="1xlzhkbpnvjhk">
                Cancel
              </Button>
              <Button onClick={handleSaveOOO} data-id="element-403" data-mp="21m9qnx8r2q0n">Save</Button>
            </div>
          </div>} data-id="element-397" data-mp="1szyluswskuar">
        
        <div className="space-y-4" data-id="element-404" data-mp="26np9dinggkfv">
          {(() => {
          const contributor = contributors.find(c => c.id === oooTargetContributorId);
          return contributor ? <div className="bg-slate-50 p-3 rounded-md border border-slate-200" data-id="element-405" data-mp="2ghfvigw40ise">
                <p className="text-sm text-slate-600" data-id="element-406" data-mp="h8r7rw1n8zd7">
                  Setting Out of Office for{' '}
                  <span className="font-semibold text-slate-900" data-id="element-407" data-mp="m7vhcnshcz42">
                    {contributor.name}
                  </span>
                </p>
              </div> : null;
        })()}

          <p className="text-sm text-slate-500" data-id="element-408" data-mp="982zxfyfb5ar">
            Select a date range for when you'll be out of office. Your entire
            column will appear dimmed during this period.
          </p>

          <div className="grid grid-cols-2 gap-4" data-id="element-409" data-mp="j6iuxu0kmypj">
            <Input type="date" label="Start Date" value={oooStartDate} onChange={e => setOOOStartDate(e.target.value)} data-id="element-410" data-mp="x2w0q5vqtbo6" />
            
            <Input type="date" label="End Date" value={oooEndDate} onChange={e => setOOOEndDate(e.target.value)} min={oooStartDate || undefined} data-id="element-411" data-mp="16u0fp5lf4wj0" />
            
          </div>

          {oooError && <p className="text-sm text-red-600" data-id="element-412" data-mp="1fs2q2br1l1iy">{oooError}</p>}

          {oooStartDate && oooEndDate && (() => {
          const today = new Date().toISOString().split('T')[0];
          const isActive = oooStartDate <= today && oooEndDate >= today;
          const isFuture = oooStartDate > today;
          const isLongLeave = isLongLeaveRange(oooStartDate, oooEndDate);
          return <div className={`p-3 rounded-md border text-sm ${isActive ? 'bg-orange-50 border-orange-200 text-orange-700' : isFuture ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`} data-id="element-413" data-mp="1qf0x85g4cqip">

                  {isActive && '⚡ OOO is currently active'}
                  {isFuture && `📅 OOO scheduled: ${new Date(oooStartDate).toLocaleDateString()} – ${new Date(oooEndDate).toLocaleDateString()}`}
                  {!isActive && !isFuture && 'This date range is in the past'}
                  {isLongLeave && <div className="mt-1 font-semibold text-purple-700" data-id="element-414" data-mp="xqv9raxoknf6">
                      ⏳ Long leave ({calendarDaysInclusive(oooStartDate, oooEndDate)} days)
                    </div>}
                </div>;
        })()}
        </div>
      </Modal>

    </div>;
}